import React, { useState, useEffect, useMemo } from "react";
import { Dialog, Card } from "@mui/material";
import { icons } from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../helpers/customHooks";
import NewButton from "../Buttons/NewButton";
import toast from "react-hot-toast";
import DeviceSelectionRowNew from "../Devices/DeviceSelectionRowNew";

const GroupModal = ({ show, groupList, handleDelete, groupSelected, type, devices, save, onClose }) => {
	const MOBILE = useIsMobile();
	const { t } = useTranslation();

	const [groupName, setGroupName] = useState("");
	const [errorText, setErrorText] = useState("");
	const [innerDevices, setInnerDevices] = useState([]);

	const handleClose = () => {
		onClose();
		setGroupName("");
		setErrorText("");
		setInnerDevices([]);
	};

	const selectedGroup = useMemo(() => {
		return groupList?.find(group => group.id === parseInt(groupSelected, 10)) || null;
	}, [groupList, groupSelected]);

	// Initialize modal state only when opening / switching mode / switching selected group
	useEffect(() => {
		if (!show || !Array.isArray(devices)) return;

		let base = devices.map(device => ({
			...device,
			selected: false,
		}));

		if (type === "edit" && selectedGroup) {
			const groupDeviceIds = selectedGroup.deviceId || [];
			setGroupName(selectedGroup.name || "");

			base = base.map(device => ({
				...device,
				selected: groupDeviceIds.includes(device.id),
			}));
		} else if (type === "create") {
			setGroupName("");
		}

		setInnerDevices(base);
		setErrorText("");
	}, [show, type, selectedGroup?.id]);

	// Merge polled device updates without losing local selections
	useEffect(() => {
		if (!show || !Array.isArray(devices)) return;

		setInnerDevices(prev => {
			if (!prev.length) return prev;

			const prevSelectedMap = new Map(
				prev.map(device => [device.id, !!device.selected])
			);

			return devices.map(device => ({
				...device,
				selected: prevSelectedMap.get(device.id) ?? false,
			}));
		});
	}, [devices, show]);

	const handleChange = (type, e) => {
		switch (type) {
			case "groupname":
				setGroupName(e.target.value);
				break;
			default:
				return;
		}
		setErrorText("");
	};

	const handleSave = (type) => {
		const checkedDevices = innerDevices
			.filter(device => device.selected)
			.map(device => device.id);

		if (checkedDevices.length === 0) {
			toast.error(t("settings.pick_one_device"));
			return;
		}

		let data = {};

		switch (type) {
			case "create":
				data = { name: groupName, deviceid: checkedDevices };
				break;
			case "edit":
				data = { groupid: groupSelected, name: groupName, deviceid: checkedDevices };
				break;
			default:
				data = {};
		}

		save(data);
	};

	const selectDevice = (id) => {
		setInnerDevices(prev =>
			prev.map(device =>
				device.id === id
					? { ...device, selected: !device.selected }
					: device
			)
		);
	};

	const flatGroupDeviceIds =
		groupList
			?.filter(group => group.id !== parseInt(groupSelected, 10))
			?.flatMap(group => group.deviceId) || [];

	const visibleDevices = innerDevices.filter(device => !flatGroupDeviceIds.includes(device.id));

	const renderForm = (title, leftButton, rightButton) => (
		<div className="p-3">
			<div className="d-flex flex-row justify-content-between align-items-center pb-3">
				<div className="d-flex align-items-center">
					<h3>{title}</h3>
				</div>
				<img className="cursor-pointer" src={icons.close_gray} onClick={handleClose} alt="..." />
			</div>

			<form>
				<div className="form-group d-flex align-items-center">
					<input
						type="text"
						className="form-control rounded24 p-3 bg-light-grey grey-blue mb-3"
						id="name"
						placeholder={t("settings.group_name")}
						value={groupName}
						onChange={(e) => handleChange("groupname", e)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
							}
						}}
					/>
					<div className="invalid-feedback ms-1 bottom">
						{errorText === "" ? "" : errorText}
					</div>
				</div>

				<div className="share_device_list scrollable-y rounded24 bg-light-grey mb-3">
					{visibleDevices.map((device, index) => (
						<DeviceSelectionRowNew
							key={device.id}
							index={index}
							text={device.name}
							selected={device.selected}
							onClick={() => selectDevice(device.id)}
						/>
					))}
				</div>
			</form>

			<div className="d-flex flex-row justify-content-between align-items-center mt-4">
				{leftButton}
				{rightButton}
			</div>
		</div>
	);

	const typeRender = () => {
		switch (type) {
			case "create":
				return renderForm(
					t("settings.create_group"),
					<NewButton onClick={handleClose} className="bg-grey p-3 rounded24 w-100 dark-grey me-2 body bold">
						{t("settings.cancel")}
					</NewButton>,
					<NewButton
						onClick={() => handleSave(type)}
						className="bg-gradient p-3 rounded24 w-100 white-white ms-2 body bold"
					>
						{t("settings.save")}
					</NewButton>
				);

			case "edit":
				return renderForm(
					t("settings.edit_group"),
					<NewButton onClick={() => handleDelete(groupSelected)} className="bg-red p-3 rounded24 w-100 white me-2 body bold">
						{t("settings.delete")}
					</NewButton>,
					<NewButton
						onClick={() => handleSave(type)}
						className="bg-gradient p-3 rounded24 w-100 white-white ms-2 body bold"
					>
						{t("settings.save")}
					</NewButton>
				);

			default:
				return null;
		}
	};

	return (
		<Dialog fullWidth={MOBILE ? true : false} open={show} onClose={handleClose}>
			<Card className="settings-dialog d-flex flex-column scrollable-y">
				<div className="w-100 h-100 grey-blue">{typeRender()}</div>
			</Card>
		</Dialog>
	);
};

export default GroupModal;